// Stockage local : trajets enregistrés et fichiers volumineux (fond de carte hors ligne).
// IndexedDB plutôt que localStorage : on y met des Blob de plusieurs mégaoctets.

const DB_NAME = 'bustracker';
const DB_VERSION = 1;
const STORE_TRIPS = 'trips';
const STORE_FILES = 'files';

let dbPromise = null;

function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_TRIPS)) {
                db.createObjectStore(STORE_TRIPS, { keyPath: 'id', autoIncrement: true });
            }
            if (!db.objectStoreNames.contains(STORE_FILES)) {
                db.createObjectStore(STORE_FILES);   // clé explicite, pas de keyPath
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return dbPromise;
}

function tx(store, mode, fn) {
    return openDb().then(db => new Promise((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        t.onabort = t.onerror = () => reject(t.error);
        t.oncomplete = () => resolve(req ? req.result : undefined);
    }));
}

// ---------------- Trajets ----------------

// La liste du menu n'a pas besoin des arrêts : on ne renvoie que les métadonnées, pour ne
// pas charger des dizaines de milliers de points à l'ouverture.
// syncId et remoteId en font partie : c'est ce qui permet d'apparier une tournée locale
// avec sa copie sur le serveur sans relire tous les trajets en entier.
async function listTrips() {
    const all = await tx(STORE_TRIPS, 'readonly', s => s.getAll());
    return (all || [])
        .map(t => ({
            id: t.id, name: t.name, stopCount: t.stopCount, createdAt: t.createdAt,
            syncId: t.syncId || null, remoteId: t.remoteId || null
        }))
        .sort((a, b) => b.createdAt - a.createdAt);
}

function getTrip(id) {
    return tx(STORE_TRIPS, 'readonly', s => s.get(id));
}

// extra porte les identifiants de synchronisation ({ syncId, remoteId }). Le store n'a
// aucun index : ajouter des champs ne demande aucune migration de DB_VERSION.
function saveTrip(name, stops, extra) {
    const stopCount = stops.filter(s => s.type !== 'start').length;
    return tx(STORE_TRIPS, 'readwrite',
        s => s.add({ name, stops, stopCount, createdAt: Date.now(), ...extra }));
}

function deleteTrip(id) {
    return tx(STORE_TRIPS, 'readwrite', s => s.delete(id));
}

// Appelée après un envoi réussi : la tournée cesse d'apparaître comme « à envoyer ».
// syncId est posé en même temps que remoteId — ils valent la même chose, l'identifiant
// venant du client (voir api.js) — pour que les trajets enregistrés AVANT l'arrivée de la
// synchronisation, qui n'ont pas de syncId, s'apparient eux aussi avec leur copie serveur.
async function markTripSynced(id, syncId) {
    const trip = await getTrip(id);
    if (!trip) return;
    trip.syncId = syncId;
    trip.remoteId = syncId;
    return tx(STORE_TRIPS, 'readwrite', s => s.put(trip));
}

async function renameTrip(id, name) {
    const trip = await getTrip(id);
    if (!trip) return;
    trip.name = name;
    return tx(STORE_TRIPS, 'readwrite', s => s.put(trip));
}

// ---------------- Fichiers ----------------

function putFile(key, blob) {
    return tx(STORE_FILES, 'readwrite', s => s.put(blob, key));
}

function getFile(key) {
    return tx(STORE_FILES, 'readonly', s => s.get(key));
}

function deleteFile(key) {
    return tx(STORE_FILES, 'readwrite', s => s.delete(key));
}

// iOS peut évincer IndexedDB après ~7 jours d'inactivité, ce qui effacerait le fond de carte.
async function requestPersistentStorage() {
    try {
        if (navigator.storage && navigator.storage.persist) return await navigator.storage.persist();
    } catch (e) {}
    return false;
}

export {
    listTrips, getTrip, saveTrip, deleteTrip, renameTrip, markTripSynced,
    putFile, getFile, deleteFile, requestPersistentStorage
};
